import { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  ListGroup,
  Collapse,
  FormCheck,
} from "react-bootstrap";
import { useAppStore } from "../stores/appStore";
import { ApiService } from "../services/apiService";
import { DEFAULT_GAME_SERVER, GAME_SERVER_OPTIONS } from "../config/gameServers";
import MigrationBackup from "../components/MigrationBackup";
import ProxyAccess from '../components/ProxyAccess';

const AccountManagement = () => {
  const {
    login,
    addAccount,
    removeAccount,
    setActiveAccount,
    refreshUserInfo,
    formatNumber,
    config,
    getRuntimeData,
  } = useAppStore();

  const runtimeData = getRuntimeData();
  const runtimeAccounts = runtimeData.accounts || {};
  const runtimeActiveAccount = runtimeData.activeAccount;

  const [showAddForm, setShowAddForm] = useState(false);
  const [loginForm, setLoginForm] = useState({
    server: DEFAULT_GAME_SERVER,
    username: "",
    password: "",
  });
  const [isAdding, setIsAdding] = useState(false);
  const [isTestingLogin, setIsTestingLogin] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => { setAccountPassword(""); }, [runtimeActiveAccount]);

  const handleAccountLogin = async () => {
    const account = runtimeAccounts[runtimeActiveAccount];
    if (!account || isLoggingIn) return;
    const password = accountPassword || account.password;
    if (!password) { alert("请先填写账号密码"); return; }
    setIsLoggingIn(true);
    try {
      const result = await login(account.server, account.username, password);
      if (!result.success) alert(`登录失败: ${result.error}`);
      else setAccountPassword("");
    } finally { setIsLoggingIn(false); }
  };

  // 批量删除相关状态
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedServerOption = GAME_SERVER_OPTIONS.some((option) => option.value === loginForm.server)
    ? loginForm.server
    : "custom";

  const handleServerOptionChange = (e) => {
    const { value } = e.target;
    setLoginForm((prev) => ({
      ...prev,
      server: value === "custom" ? "" : value,
    }));
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    const serverAddress = loginForm.server.trim();
    if (!serverAddress || !loginForm.username || !loginForm.password) {
      return;
    }

    setIsAdding(true);
    try {
      // 生成账号键
      const accountKey = `${loginForm.username}_S${serverAddress}`;

      // 检查账号是否已存在
      if (runtimeAccounts[accountKey]) {
        alert(`账号已存在: ${loginForm.username} (${serverAddress})`);
        return;
      }

      // 直接添加账号，不进行登录
      addAccount(loginForm.username, serverAddress, loginForm.password, null, null);
      setLoginForm({ server: DEFAULT_GAME_SERVER, username: "", password: "" });
      setShowAddForm(false);
    } finally {
      setIsAdding(false);
    }
  };

  const handleTestLogin = async () => {
    const serverAddress = loginForm.server.trim();
    if (!serverAddress || !loginForm.username || !loginForm.password) {
      return;
    }

    setIsTestingLogin(true);
    try {
      // 创建临时ApiService实例进行测试登录
      const apiService = new ApiService(config);
      const result = await apiService.login(
        serverAddress,
        loginForm.username,
        loginForm.password
      );

      if (result.success) {
        alert(
          `登录测试成功！\n用户: ${
            result.userInfo?.nickname || loginForm.username
          }\n等级: ${result.userInfo?.level || "-"}`
        );
      } else {
        alert(`登录测试失败！\n错误信息: ${result.error}`);
      }
    } catch (error) {
      alert(`登录测试异常！\n错误信息: ${error.message}`);
    } finally {
      setIsTestingLogin(false);
    }
  };

  // 批量删除相关函数
  const handleSelectAccount = (accountKey) => {
    const newSelected = new Set(selectedAccounts);
    if (newSelected.has(accountKey)) {
      newSelected.delete(accountKey);
    } else {
      newSelected.add(accountKey);
    }
    setSelectedAccounts(newSelected);
    setIsSelectAll(newSelected.size === Object.keys(runtimeAccounts).length);
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      setSelectedAccounts(new Set());
      setIsSelectAll(false);
    } else {
      setSelectedAccounts(new Set(Object.keys(runtimeAccounts)));
      setIsSelectAll(true);
    }
  };

  const handleBatchDelete = () => {
    if (selectedAccounts.size === 0) {
      alert("请先选择要删除的账号");
      return;
    }

    const accountList = Array.from(selectedAccounts)
      .map(accountKey => {
        const account = runtimeAccounts[accountKey];
        return account ? `${account.username} (${account.server})` : accountKey;
      })
      .join(", ");

    if (window.confirm(`确定要删除以下 ${selectedAccounts.size} 个账号吗？\n${accountList}`)) {
      // 批量删除选中的账号
      selectedAccounts.forEach(accountKey => {
        removeAccount(accountKey);
      });

      // 清空选择状态
      setSelectedAccounts(new Set());
      setIsSelectAll(false);

      alert(`成功删除 ${selectedAccounts.size} 个账号`);
    }
  };

  const handleRemoveAccount = (accountKey) => {
    const account = runtimeAccounts[accountKey];
    const displayName = account ? `${account.username} (${account.server})` : accountKey;

    if (window.confirm(`确定要删除账号 ${displayName} 吗？`)) {
      removeAccount(accountKey);
      // 如果删除的账号在选中列表中，从选中列表中移除
      if (selectedAccounts.has(accountKey)) {
        const newSelected = new Set(selectedAccounts);
        newSelected.delete(accountKey);
        setSelectedAccounts(newSelected);
        setIsSelectAll(newSelected.size === Object.keys(runtimeAccounts).length - 1);
      }
    }
  };

  const handleRefreshUserInfo = async () => {
    if (!runtimeActiveAccount) return;

    const account = runtimeAccounts[runtimeActiveAccount];
    if (!account) return;

    // 如果没有token，先尝试登录
    if (!account.token) {
      if (!account.password) {
        alert("请先在账号密码区域补填密码并手动登录");
        return;
      }
      const loginResult = await login(
        account.server,
        account.username,
        account.password
      );
      if (!loginResult.success) {
        return; // 登录失败，不继续刷新
      }
    }

    // 刷新用户信息
    refreshUserInfo(runtimeActiveAccount);
  };

  // 导出账号
  const handleExportAccounts = () => {
    if (Object.keys(runtimeAccounts).length === 0) {
      alert("暂无账号可导出");
      return;
    }

    const exportData = Object.keys(runtimeAccounts)
      .map((accountKey) => {
        const account = runtimeAccounts[accountKey];
        return `${account.server},${account.username},${account.password}`;
      })
      .join("\n");

    const blob = new Blob([exportData], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `accounts_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    alert(`成功导出 ${Object.keys(runtimeAccounts).length} 个账号`);
  };

  // 导入账号
  const handleImportAccounts = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        const lines = content.split("\n").filter((line) => line.trim());
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        lines.forEach((line, index) => {
          const parts = line.trim().split(",");
          if (parts.length !== 3) {
            errorCount++;
            errors.push(`第${index + 1}行格式错误: ${line}`);
            return;
          }

          const [server, username, password] = parts.map((part) => part.trim());

          if (!server || !username || !password) {
            errorCount++;
            errors.push(`第${index + 1}行数据不完整: ${line}`);
            return;
          }

          // 生成账号键并检查是否已存在
          const accountKey = `${username}_S${server}`;

          if (runtimeAccounts[accountKey]) {
            errorCount++;
            errors.push(
              `第${index + 1}行账号已存在: ${username} (${server})`
            );
            return;
          }

          // 添加账号
          addAccount(username, server, password, null, null);
          successCount++;
        });

        let message = `导入完成！\n成功: ${successCount} 个账号`;
        if (errorCount > 0) {
          message += `\n失败: ${errorCount} 个账号`;
          if (errors.length > 0) {
            message += `\n\n错误详情:\n${errors.slice(0, 5).join("\n")}`;
            if (errors.length > 5) {
              message += `\n... 还有 ${errors.length - 5} 个错误`;
            }
          }
        }
        alert(message);
      } catch (error) {
        alert(`导入失败: ${error.message}`);
      } finally {
        setIsImporting(false);
        // 清空文件选择
        event.target.value = "";
      }
    };

    reader.onerror = () => {
      alert("文件读取失败");
      setIsImporting(false);
      event.target.value = "";
    };

    reader.readAsText(file, "utf-8");
  };

  const activeAccountData = runtimeActiveAccount ? runtimeAccounts[runtimeActiveAccount] : null;

  return (
    <Container fluid>
      <MigrationBackup />
      <ProxyAccess />
      {/* 页面标题和说明 */}
      <Row className="mb-4">
        <Col xs={12}>
          <Card>
            <Card.Body className="text-center">
              <h4 className="card-title text-primary">账号管理中心</h4>
              <p className="card-text text-muted">
                管理您的游戏账号，支持多账号切换和统一管理
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 账号管理 */}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">账号列表</h5>
            {Object.keys(runtimeAccounts).length > 0 && (
              <div className="d-flex align-items-center gap-2">
                <FormCheck
                  type="checkbox"
                  checked={isSelectAll}
                  onChange={handleSelectAll}
                  label="全选"
                />
                {selectedAccounts.size > 0 && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleBatchDelete}
                  >
                    <i>🗑️</i> 批量删除 ({selectedAccounts.size})
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <ListGroup className="mb-3">
            {Object.keys(runtimeAccounts).length === 0 ? (
              <ListGroup.Item className="text-center text-muted">
                暂无账号，请添加新账号
              </ListGroup.Item>
            ) : (
              Object.keys(runtimeAccounts).map((accountKey) => {
                const account = runtimeAccounts[accountKey];
                return (
                  <ListGroup.Item
                    as="div"
                    role="button"
                    key={accountKey}
                    action
                    active={runtimeActiveAccount === accountKey}
                    onClick={() => {
                      setActiveAccount(accountKey);
                    }}
                    tabIndex={0}
                    onKeyDown={event => {
                      if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        setActiveAccount(accountKey);
                      }
                    }}
                    className="d-flex justify-content-between align-items-center"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FormCheck
                        type="checkbox"
                        checked={selectedAccounts.has(accountKey)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectAccount(accountKey);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>
                        {account.username} ({account.server})
                      </span>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveAccount(accountKey);
                      }}
                    >
                      删除
                    </Button>
                  </ListGroup.Item>
                );
              })
            )}
          </ListGroup>

          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="primary"
              onClick={() => setShowAddForm(!showAddForm)}
              aria-expanded={showAddForm}
            >
              <i>➕</i> 添加新账号
            </Button>

            <Button
              variant="outline-success"
              onClick={handleExportAccounts}
              disabled={Object.keys(runtimeAccounts).length === 0}
            >
              <i>📤</i> 导出账号
            </Button>

            <Button
              variant="outline-info"
              as="label"
              disabled={isImporting}
              style={{ cursor: isImporting ? "not-allowed" : "pointer" }}
            >
              <i>📥</i> {isImporting ? "导入中..." : "导入账号"}
              <input
                type="file"
                accept=".txt"
                onChange={handleImportAccounts}
                style={{ display: "none" }}
                disabled={isImporting}
              />
            </Button>
          </div>

          <Collapse in={showAddForm}>
            <div className="mt-3">
              <Card>
                <Card.Body>
                  <Form onSubmit={handleAddAccount}>
                    <Row>
                      <Col md={4} className="mb-3">
                        <Form.Label>服务器</Form.Label>
                        <Form.Select
                          name="server"
                          value={selectedServerOption}
                          onChange={handleServerOptionChange}
                        >
                          {GAME_SERVER_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                          <option value="custom">自定义服务器</option>
                        </Form.Select>
                        {selectedServerOption === "custom" && (
                          <Form.Control
                            type="text"
                            name="server"
                            placeholder="输入服务器地址"
                            required
                            value={loginForm.server}
                            onChange={handleInputChange}
                            className="mt-2"
                          />
                        )}
                      </Col>
                      <Col md={4} className="mb-3">
                        <Form.Label>用户名</Form.Label>
                        <Form.Control
                          type="text"
                          name="username"
                          placeholder="输入用户名"
                          required
                          value={loginForm.username}
                          onChange={handleInputChange}
                        />
                      </Col>
                      <Col md={4} className="mb-3">
                        <Form.Label>密码</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          placeholder="输入密码"
                          required
                          value={loginForm.password}
                          onChange={handleInputChange}
                        />
                      </Col>
                    </Row>
                    <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                      <Button
                        type="button"
                        variant="outline-primary"
                        disabled={
                          isTestingLogin ||
                          !loginForm.server ||
                          !loginForm.username ||
                          !loginForm.password
                        }
                        onClick={handleTestLogin}
                      >
                        {isTestingLogin ? "测试中..." : "测试登录"}
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isAdding}
                      >
                        {isAdding ? "添加中..." : "添加账号"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </div>
          </Collapse>
        </Card.Body>
      </Card>

      {/* 当前活动账号信息 */}
      {activeAccountData && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">当前账号: {activeAccountData ? `${activeAccountData.username} (${activeAccountData.server})` : runtimeActiveAccount}</h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="active-account-password">账号密码</Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                <Form.Control id="active-account-password" type="password" autoComplete="new-password"
                  placeholder={activeAccountData.password ? "已保存密码；填写可更新" : "迁移后请补填密码"}
                  value={accountPassword} onChange={event => setAccountPassword(event.target.value)}
                  disabled={isLoggingIn} style={{ maxWidth: 360 }} />
                <Button onClick={handleAccountLogin} disabled={isLoggingIn || (!accountPassword && !activeAccountData.password)}>
                  {isLoggingIn ? "登录中..." : "登录 / 更新密码"}
                </Button>
              </div>
            </Form.Group>
            <Row>
              <Col md={6}>
                <div className="user-info-item">
                  <span className="user-info-label">昵称:</span>
                  <span className="user-info-value">
                    {activeAccountData.userInfo?.nickname || "-"}
                  </span>
                </div>
                <div className="user-info-item">
                  <span className="user-info-label">等级:</span>
                  <span className="user-info-value">
                    {activeAccountData.userInfo?.level || "-"}
                  </span>
                </div>
                <div className="user-info-item">
                  <span className="user-info-label">VIP等级:</span>
                  <span className="user-info-value">
                    {activeAccountData.userInfo?.vip || "-"}
                  </span>
                </div>
              </Col>
              <Col md={6}>
                <div className="user-info-item">
                  <span className="user-info-label">银币:</span>
                  <span className="user-info-value">
                    {activeAccountData.userInfo?.silver
                      ? formatNumber(activeAccountData.userInfo.silver)
                      : "-"}
                  </span>
                </div>
                <div className="user-info-item">
                  <span className="user-info-label">元宝:</span>
                  <span className="user-info-value">
                    {activeAccountData.userInfo?.gold
                      ? formatNumber(activeAccountData.userInfo.gold)
                      : "-"}
                  </span>
                </div>
                <div className="user-info-item">
                  <span className="user-info-label">体力:</span>
                  <span className="user-info-value">
                    {activeAccountData.userInfo?.act
                      ? formatNumber(activeAccountData.userInfo.act)
                      : "-"}
                  </span>
                </div>
              </Col>
            </Row>
            <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={handleRefreshUserInfo}
              >
                <i>🔄</i> 刷新信息
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default AccountManagement;
